import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "./ui/dialog";
import { Heart } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";

export default function QuickView({ product, open, onOpenChange }) {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [size, setSize] = useState(null);

  useEffect(() => {
    setSize(null);
  }, [product]);

  if (!product) return null;
  const saved = inWishlist(product.slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 border-line bg-ivory overflow-hidden" data-testid="quick-view-dialog">
        <div className="grid md:grid-cols-2">
          <div className="aspect-[3/4] bg-[#efece6]">
            <img src={product.primary_image} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="p-8 flex flex-col">
            <p className="overline text-gold-deep mb-2">{product.fabric} · {product.occasion}</p>
            <h2 className="font-serif text-3xl leading-tight">{product.name}</h2>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-lg">{inr(product.price)}</span>
              {product.compare_at_price && (
                <span className="text-ink-soft line-through">{inr(product.compare_at_price)}</span>
              )}
            </div>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed line-clamp-4">
              {(product.description || "")
                .replace(/^\s*[-•*]\s+/gm, "")
                .replace(/^\s*\d+\.\s+/gm, "")
                .replace(/\*\*(.+?)\*\*/g, "$1")
                .replace(/\*(.+?)\*/g, "$1")
                .replace(/_(.+?)_/g, "$1")
                .replace(/\n+/g, " · ")}
            </p>

            <div className="mt-6">
              <p className="overline mb-3">Select Size</p>
              <div className="flex flex-wrap gap-2">
                {(product.sizes || []).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`h-10 w-12 border text-sm transition-colors ${
                      size === s ? "border-ink bg-ink text-ivory" : "border-line hover:border-ink"
                    }`}
                    data-testid={`qv-size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-auto pt-8 flex items-center gap-3">
              <button
                disabled={!size}
                onClick={() => {
                  addToCart(product, size);
                  onOpenChange(false);
                }}
                className="btn-ink flex-1 py-3.5 overline disabled:opacity-40"
                data-testid="qv-add-to-cart"
              >
                {size ? "Add to Bag" : "Select a size"}
              </button>
              <button
                onClick={() => toggleWishlist(product)}
                className="grid h-12 w-12 place-items-center border border-line hover:border-ink transition-colors"
                data-testid="qv-wishlist"
              >
                <Heart size={16} fill={saved ? "currentColor" : "none"} />
              </button>
            </div>
            <Link to={`/product/${product.slug}`} onClick={() => onOpenChange(false)} className="mt-4 overline link-underline text-center">
              View full details
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
